import React, { useEffect, useState } from "react";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE } from "../../../ApiUrl";
import { useNavigate, useParams } from "react-router-dom";
import { FiSave, FiX, FiArrowLeft } from "react-icons/fi";
import "./AddFormField.css";

function AddFormField() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditMode);
  const [modules, setModules] = useState([]);
  const [originalRecord, setOriginalRecord] = useState(null);

  const [formData, setFormData] = useState({
    field_code: "",
    field_label: "",
    purpose: "",
    data_type: "STRING",
    ui_control: "",
    is_required: true,
    is_repeatable: false,
    option_set: "",
    options_schema: "",
    validation_schema: "",
    evidence_rule: "",
    resume_token: "",
    default_resume_visibility: "Never",
    data_classification: "Public",
    is_active: true,
    form_module: "",
  });

  useEffect(() => {
    fetchModules();

    if (isEditMode) {
      fetchField();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchModules = async () => {
    try {
      const res = await axios.get(`${API_BASE}/form-modules/`);
      setModules(res.data?.data || []);
    } catch (error) {
      console.error("Error fetching form modules:", error);
      Swal.fire(
        "Error",
        "Failed to load form module list",
        "error"
      );
    }
  };

  const fetchField = async () => {
    setFetching(true);

    try {
      const res = await axios.get(`${API_BASE}/form-fields/${id}/`);
      const item = res.data?.data || res.data;

      setOriginalRecord(item);

      setFormData({
        field_code: item.field_code || "",
        field_label: item.field_label || "",
        purpose: item.purpose || "",
        data_type: item.data_type || "STRING",
        ui_control: item.ui_control || "",
        is_required: item.is_required ?? true,
        is_repeatable: item.is_repeatable ?? false,
        option_set: item.option_set || "",
        options_schema: item.options_schema || "",
        validation_schema: item.validation_schema || "",
        evidence_rule: item.evidence_rule || "",
        resume_token: item.resume_token || "",
        default_resume_visibility:
          item.default_resume_visibility || "Never",
        data_classification:
          item.data_classification || "Public",
        is_active: item.is_active ?? true,
        form_module: item.form_module ?? "",
      });
    } catch (error) {
      console.error("Error fetching form field:", error);

      Swal.fire(
        "Error",
        "Failed to load form field details",
        "error"
      );

      navigate("/catalog/form-fields");
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleField = (name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.field_code.trim() ||
      !formData.field_label.trim()
    ) {
      Swal.fire(
        "Validation Error",
        "Field Code and Field Label are required",
        "warning"
      );
      return;
    }

    if (!formData.form_module) {
      Swal.fire(
        "Validation Error",
        "Please select a Form Module",
        "warning"
      );
      return;
    }

    setLoading(true);

    try {
      const payload = {
        field_code: formData.field_code.trim(),
        field_label: formData.field_label.trim(),
        purpose: formData.purpose.trim(),
        data_type: formData.data_type,
        ui_control: formData.ui_control.trim(),
        is_required: formData.is_required,
        is_repeatable: formData.is_repeatable,
        option_set: formData.option_set.trim(),
        options_schema: formData.options_schema.trim(),
        validation_schema: formData.validation_schema.trim(),
        evidence_rule: formData.evidence_rule.trim(),
        resume_token: formData.resume_token.trim(),
        default_resume_visibility:
          formData.default_resume_visibility,
        data_classification: formData.data_classification,
        sequence: originalRecord?.sequence ?? 0,
        is_active: formData.is_active,
        form_module: Number(formData.form_module),
        created_by: originalRecord?.created_by ?? 1,
      };

      if (isEditMode) {
        await axios.put(
          `${API_BASE}/form-fields/${id}/`,
          payload
        );

        Swal.fire({
          icon: "success",
          title: "Updated!",
          text: "Form field has been updated.",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        await axios.post(
          `${API_BASE}/form-fields/`,
          payload
        );

        Swal.fire({
          icon: "success",
          title: "Added!",
          text: "Form field has been created.",
          timer: 2000,
          showConfirmButton: false,
        });
      }

      navigate("/catalog/form-fields");
    } catch (error) {
      console.error(
        `Error ${
          isEditMode ? "updating" : "creating"
        } form field:`,
        error
      );

      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          `Failed to ${
            isEditMode ? "update" : "create"
          } form field`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/catalog/form-fields");
  };

  if (fetching) {
    return (
      <div className="app-layout">
        <Sidebar />

        <div className="main-content">
          <Header />

          <div className="form-field-form-page">
            <div className="form-field-form-loader">
              <div className="form-field-form-spinner"></div>
              <p>Loading form field...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="form-field-form-page">
          <div className="form-field-form-top">
            <button
              type="button"
              className="form-field-form-back-btn"
              onClick={handleBack}
              title="Back"
            >
              <FiArrowLeft />
            </button>

            <div className="form-field-form-page-heading">
              <h2>
                {isEditMode
                  ? "Edit Form Field"
                  : "Add Form Field"}
              </h2>
            </div>
          </div>

          <div className="form-field-form-card">
            <form onSubmit={handleSubmit}>
              <div className="form-field-form-grid">
                <div className="form-field-form-field">
                  <label htmlFor="field_code">
                    Field Code{" "}
                    <span className="form-field-form-required">
                      *
                    </span>
                  </label>

                  <input
                    type="text"
                    id="field_code"
                    name="field_code"
                    placeholder="e.g. FLD-001"
                    value={formData.field_code}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-field-form-field">
                  <label htmlFor="field_label">
                    Field Label{" "}
                    <span className="form-field-form-required">
                      *
                    </span>
                  </label>

                  <input
                    type="text"
                    id="field_label"
                    name="field_label"
                    placeholder="e.g. Site Address"
                    value={formData.field_label}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-field-form-field">
                  <label htmlFor="form_module">
                    Form Module{" "}
                    <span className="form-field-form-required">
                      *
                    </span>
                  </label>

                  <select
                    id="form_module"
                    name="form_module"
                    value={formData.form_module}
                    onChange={handleChange}
                    required
                  >
                    <option value="">
                      Select form module
                    </option>

                    {modules.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.module_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-field-form-field">
                  <label htmlFor="data_type">Data Type</label>

                  <select
                    id="data_type"
                    name="data_type"
                    value={formData.data_type}
                    onChange={handleChange}
                  >
                    <option value="STRING">String</option>
                    <option value="NUMBER">Number</option>
                    <option value="BOOLEAN">Boolean</option>
                    <option value="DATE">Date</option>
                    <option value="TEXT">Text</option>
                    <option value="FILE">File</option>
                  </select>
                </div>

                <div className="form-field-form-field">
                  <label htmlFor="ui_control">
                    UI Control
                  </label>

                  <input
                    type="text"
                    id="ui_control"
                    name="ui_control"
                    placeholder="e.g. text_input, dropdown, checkbox"
                    value={formData.ui_control}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-field-form-field">
                  <label htmlFor="option_set">
                    Option Set
                  </label>

                  <input
                    type="text"
                    id="option_set"
                    name="option_set"
                    placeholder="e.g. EVIDENCE_TYPE"
                    value={formData.option_set}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-field-form-field">
                  <label htmlFor="resume_token">
                    Resume Token
                  </label>

                  <input
                    type="text"
                    id="resume_token"
                    name="resume_token"
                    placeholder="e.g. {{site_address}}"
                    value={formData.resume_token}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-field-form-field">
                  <label htmlFor="default_resume_visibility">
                    Default Resume Visibility
                  </label>

                  <select
                    id="default_resume_visibility"
                    name="default_resume_visibility"
                    value={formData.default_resume_visibility}
                    onChange={handleChange}
                  >
                    <option value="Never">Never</option>
                    <option value="Always">Always</option>
                    <option value="Optional">Optional</option>
                    <option value="Client Specific">
                      Client Specific
                    </option>
                  </select>
                </div>

                <div className="form-field-form-field">
                  <label htmlFor="data_classification">
                    Data Classification
                  </label>

                  <select
                    id="data_classification"
                    name="data_classification"
                    value={formData.data_classification}
                    onChange={handleChange}
                  >
                    <option value="Public">Public</option>
                    <option value="Professional">
                      Professional
                    </option>
                    <option value="Sensitive PII">
                      Sensitive PII
                    </option>
                    <option value="Restricted PII">
                      Restricted PII
                    </option>
                    <option value="Commercial">
                      Commercial
                    </option>
                    <option value="Health">Health</option>
                  </select>
                </div>

                <div className="form-field-form-field form-field-form-field-full">
                  <label htmlFor="purpose">Purpose</label>

                  <textarea
                    id="purpose"
                    name="purpose"
                    placeholder="What this field is used for..."
                    value={formData.purpose}
                    onChange={handleChange}
                    rows={3}
                  />
                </div>

                <div className="form-field-form-field form-field-form-field-full">
                  <label htmlFor="options_schema">
                    Options Schema
                  </label>

                  <textarea
                    id="options_schema"
                    name="options_schema"
                    placeholder="JSON schema for options..."
                    value={formData.options_schema}
                    onChange={handleChange}
                    rows={3}
                  />
                </div>

                <div className="form-field-form-field form-field-form-field-full">
                  <label htmlFor="validation_schema">
                    Validation Schema
                  </label>

                  <textarea
                    id="validation_schema"
                    name="validation_schema"
                    placeholder="JSON schema for validation..."
                    value={formData.validation_schema}
                    onChange={handleChange}
                    rows={3}
                  />
                </div>

                <div className="form-field-form-field form-field-form-field-full">
                  <label htmlFor="evidence_rule">
                    Evidence Rule
                  </label>

                  <textarea
                    id="evidence_rule"
                    name="evidence_rule"
                    placeholder="Rule describing required evidence..."
                    value={formData.evidence_rule}
                    onChange={handleChange}
                    rows={3}
                  />
                </div>
              </div>

              <div className="form-field-form-toggle-group">
                <div className="form-field-form-toggle-row">
                  <button
                    type="button"
                    className={`form-field-form-toggle-switch ${
                      formData.is_required
                        ? "form-field-form-toggle-on"
                        : ""
                    }`}
                    onClick={() =>
                      toggleField("is_required")
                    }
                    aria-pressed={formData.is_required}
                  >
                    <span className="form-field-form-toggle-knob" />
                  </button>

                  <span className="form-field-form-toggle-label">
                    Required
                  </span>
                </div>

                <div className="form-field-form-toggle-row">
                  <button
                    type="button"
                    className={`form-field-form-toggle-switch ${
                      formData.is_repeatable
                        ? "form-field-form-toggle-on"
                        : ""
                    }`}
                    onClick={() =>
                      toggleField("is_repeatable")
                    }
                    aria-pressed={formData.is_repeatable}
                  >
                    <span className="form-field-form-toggle-knob" />
                  </button>

                  <span className="form-field-form-toggle-label">
                    Repeatable
                  </span>
                </div>

                <div className="form-field-form-toggle-row">
                  <button
                    type="button"
                    className={`form-field-form-toggle-switch ${
                      formData.is_active
                        ? "form-field-form-toggle-on"
                        : ""
                    }`}
                    onClick={() => toggleField("is_active")}
                    aria-pressed={formData.is_active}
                  >
                    <span className="form-field-form-toggle-knob" />
                  </button>

                  <span className="form-field-form-toggle-label">
                    Active
                  </span>
                </div>
              </div>

              <div className="form-field-form-actions">
                <button
                  type="button"
                  className="form-field-form-cancel-btn"
                  onClick={handleBack}
                  disabled={loading}
                >
                  <FiX />
                  Cancel
                </button>

                <button
                  type="submit"
                  className="form-field-form-save-btn"
                  disabled={loading}
                >
                  <FiSave />

                  {loading
                    ? "Saving..."
                    : isEditMode
                    ? "Update Field"
                    : "Save Field"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddFormField;